import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Pagination,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import { useAppDispatch, useAppSelector } from "app/hooks";
import TemplateToolbar from "../TemplateToolbar";
import {
  loadZnsTemplates,
  selectZnsTemplatesResults,
  selectZnsTemplatesTotalCount,
  selectZnsTemplatesLoading,
  selectZnsTemplatesError,
} from "../../store/znsTemplateSlice";
import type {
  IZnsTemplate,
  ZnsTemplateStatus,
  ZnsTemplateTag,
} from "modules/outbound-message/types/znsTemplate";
import Loading from "components/Common/Loading";
import moment from "moment";

const PAGE_SIZE = 8;
export interface TemplatePickerFilters {
  tag: "" | ZnsTemplateTag;
  status: "" | ZnsTemplateStatus;
  createdDateFrom: string;
  createdDateTo: string;
  pulledDateFrom: string;
  pulledDateTo: string;
  name: string;
}

/** Default filter values for the template picker. */
const DEFAULT_PICKER_FILTERS: TemplatePickerFilters = {
  tag: "",
  status: "",
  createdDateFrom: "",
  createdDateTo: "",
  pulledDateFrom: "",
  pulledDateTo: "",
  name: "",
};

/** Nominal size of the preview iframe in each grid card. */
const CARD_PREVIEW_WIDTH = 200;
const CARD_PREVIEW_HEIGHT = 320;

export interface TemplatePickerDialogProps {
  /** Whether the dialog is open. */
  open: boolean;
  /** Called when the dialog should close. */
  onClose: () => void;
  /** Called when user selects a template (dialog typically closes after). */
  onSelectTemplate: (template: IZnsTemplate) => void;

  /** Selected template. */
  selectedTemplate: IZnsTemplate | null;
}

/**
 * Dialog that lists ZNS templates with filters and a 4-column x 2-row grid.
 * Uses the same list API as FetchMessageTemplatePage with page size 8.
 */
const TemplatePickerDialog: React.FC<TemplatePickerDialogProps> = ({
  open,
  onClose,
  onSelectTemplate,
  selectedTemplate,
}) => {
  const dispatch = useAppDispatch();
  const results = useAppSelector(selectZnsTemplatesResults);
  const totalCount = useAppSelector(selectZnsTemplatesTotalCount);
  const loading = useAppSelector(selectZnsTemplatesLoading);
  const error = useAppSelector(selectZnsTemplatesError);

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<TemplatePickerFilters>(
    DEFAULT_PICKER_FILTERS,
  );

  const totalPages = Math.ceil(totalCount / PAGE_SIZE) || 1;

  const fetchTemplates = () => {
    dispatch(
      loadZnsTemplates({
        PageNumber: page,
        PageSize: PAGE_SIZE,
        ...(filters.tag ? { Tag: filters.tag } : {}),
        ...(filters.status ? { Status: filters.status } : {}),
        ...(filters.createdDateFrom
          ? { CreatedDateFrom: filters.createdDateFrom }
          : {}),
        ...(filters.createdDateTo
          ? { CreatedDateTo: filters.createdDateTo }
          : {}),
        ...(filters.pulledDateFrom
          ? { PulledDateFrom: filters.pulledDateFrom }
          : {}),
        ...(filters.pulledDateTo ? { PulledDateTo: filters.pulledDateTo } : {}),
        ...(filters.name ? { Name: filters.name } : {}),
      }),
    );
  };

  useEffect(() => {
    if (open) {
      fetchTemplates();
    }
  }, [
    open,
    page,
    filters.tag,
    filters.status,
    filters.createdDateFrom,
    filters.createdDateTo,
    filters.pulledDateFrom,
    filters.pulledDateTo,
    filters.name,
  ]);

  const handleTagChange = (tag: "" | ZnsTemplateTag) => {
    setFilters((prev) => ({ ...prev, tag }));
    setPage(1);
  };

  const handleStatusChange = (status: "" | ZnsTemplateStatus) => {
    setFilters((prev) => ({ ...prev, status }));
    setPage(1);
  };

  const handleDateRangeChange = (from: string, to: string) => {
    setFilters((prev) => ({
      ...prev,
      createdDateFrom: from,
      createdDateTo: to,
    }));
    setPage(1);
  };

  const handlePulledDateRangeChange = (from: string, to: string) => {
    setFilters((prev) => ({
      ...prev,
      pulledDateFrom: from,
      pulledDateTo: to,
    }));
    setPage(1);
  };

  const handleSearchChange = (name: string) => {
    setFilters((prev) => ({ ...prev, name }));
    setPage(1);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_PICKER_FILTERS);
    setPage(1);
  };

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  const handleSelect = (template: IZnsTemplate) => {
    onSelectTemplate(template);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: { maxHeight: "90vh" },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          pr: 1,
        }}
      >
        <Typography variant="h6">Select ZNS template</Typography>
        <IconButton aria-label="Close" onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column" }}>
          <TemplateToolbar
            tag={filters.tag}
            onTagChange={handleTagChange}
            status={filters.status}
            onStatusChange={handleStatusChange}
            dateRange={{
              from: filters.createdDateFrom,
              to: filters.createdDateTo,
            }}
            onDateRangeChange={handleDateRangeChange}
            pulledDateRange={{
              from: filters.pulledDateFrom,
              to: filters.pulledDateTo,
            }}
            onPulledDateRangeChange={handlePulledDateRangeChange}
            search={filters.name}
            onSearchChange={handleSearchChange}
            onReset={handleResetFilters}
          />
          {error && (
            <Typography color="error">
              {error?.Message ?? "Failed to load templates"}
            </Typography>
          )}
          {loading ? (
            <Loading />
          ) : (
            <>
              <Box sx={{ height: "70%" }}>
                <Grid container spacing={2} sx={{}}>
                  {results.slice(0, 8).map((template) => (
                    <Grid item xs={12} sm={6} md={3} key={template.id}>
                      <Card
                        variant="outlined"
                        sx={{
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          borderWidth: 1,
                          borderColor: "grey.400",
                          ...(selectedTemplate?.templateId ===
                            template.templateId && {
                            borderColor: "primary.main",
                            borderWidth: 2,
                          }),
                        }}
                      >
                        <CardActionArea
                          onClick={() => handleSelect(template)}
                          sx={{
                            flex: 1,
                            display: "flex",
                            alignItems: "stretch",
                          }}
                        >
                          <CardContent sx={{ flex: 1, width: "100%" }}>
                            <Box>
                              <Box
                                component="iframe"
                                src={template.previewUrl}
                                title={template.templateName}
                                sx={{
                                  width: "100%",
                                  height: 294,
                                  border: "none",
                                  borderRadius: 1,
                                  display: "block",
                                }}
                              />
                            </Box>
                            <Typography
                              variant="subtitle1"
                              fontWeight="bold"
                              title={template.templateName}
                              sx={{
                                display: "-webkit-box",
                                WebkitBoxOrient: "vertical",
                                WebkitLineClamp: 2,
                                overflow: "hidden",
                                height: "56px",
                              }}
                            >
                              {template.templateName}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mt: 0.5 }}
                            >
                              {`ZNS ID: ${template.templateId}`}
                            </Typography>
                            <Box
                              sx={{
                                mt: 1,
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                                color: "primary.main",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography variant="body2" color="text.primary">
                                {Math.round(Number(template.priceSdt))}đ / ZNS
                              </Typography>
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => handleSelect(template)}
                              >
                                Select
                              </Button>
                            </Box>
                          </CardContent>
                        </CardActionArea>
                      </Card>
                    </Grid>
                  ))}
                </Grid>
              </Box>
              {totalPages > 1 && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    py: 2,
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(TemplatePickerDialog);
